import type { MutableRefObject } from 'react'
import { Platform } from 'react-native'
import Purchases from 'react-native-purchases'

import { addBreadcrumb } from '../utils/sentry'
import {
  getStoredRevenueCatMixpanelDistinctId,
  isRevenueCatMixpanelDistinctIdImmutableError,
  markRevenueCatMixpanelDistinctIdSynced,
} from './revenuecat-mixpanel-sync'
import { normalizeRevenueCatId } from './revenuecat-ownership'

interface SyncProfileParams {
  userId: string | null
  userEmail: string | null
  userName: string | null
  prevUserIdRef: MutableRefObject<string | null>
  prevEmailRef: MutableRefObject<string | null>
  prevNameRef: MutableRefObject<string | null>
}

export async function syncRevenueCatProfile({
  userId,
  userEmail,
  userName,
  prevUserIdRef,
  prevEmailRef,
  prevNameRef,
}: SyncProfileParams): Promise<void> {
  if (Platform.OS === 'web') {
    addBreadcrumb('purchase', 'revenuecat_profile_sync_skipped_web', {}, 'info')
    return
  }

  if (!userId || prevUserIdRef.current !== userId) return

  const shouldSyncEmail =
    userEmail != null &&
    userEmail.length > 0 &&
    prevEmailRef.current !== userEmail
  const shouldSyncName =
    userName != null && userName.length > 0 && prevNameRef.current !== userName

  if (!shouldSyncEmail && !shouldSyncName) return

  try {
    if (shouldSyncEmail) {
      await Purchases.setEmail(userEmail)
      prevEmailRef.current = userEmail
    }
    if (shouldSyncName) {
      await Purchases.setDisplayName(userName)
      prevNameRef.current = userName
    }
  } catch (error) {
    console.warn('Failed to sync RevenueCat user profile', error)
  }
}

interface SyncMixpanelParams {
  userId: string | null
  mixpanelId: string | null
  authUserId: string | null
}

export async function syncRevenueCatMixpanelDistinctId({
  userId,
  mixpanelId,
  authUserId,
}: SyncMixpanelParams): Promise<void> {
  if (Platform.OS === 'web' || userId == null) {
    return
  }

  const normalizedMixpanelId = normalizeRevenueCatId(mixpanelId)
  if (normalizedMixpanelId == null) {
    addBreadcrumb(
      'purchase',
      'revenuecat_mixpanel_id_missing',
      {
        platform: Platform.OS,
        authUserIdPresent: authUserId != null,
        revenueCatIdPresent: true,
        mixpanelIdPresent: false,
      },
      'warning',
    )
    console.warn(
      'RevenueCat Mixpanel distinct ID missing; purchase events may not merge in Mixpanel.',
    )
    return
  }

  try {
    const storedMixpanelId = await getStoredRevenueCatMixpanelDistinctId(userId)

    if (storedMixpanelId === normalizedMixpanelId) {
      return
    }

    if (storedMixpanelId != null) {
      addBreadcrumb(
        'purchase',
        'revenuecat_mixpanel_id_resync_skipped',
        { revenueCatId: userId },
        'warning',
      )
      console.warn(
        'RevenueCat Mixpanel distinct ID already synced with a different value; skipping re-sync.',
      )
      return
    }

    await Purchases.setMixpanelDistinctID(normalizedMixpanelId)
    await markRevenueCatMixpanelDistinctIdSynced({
      revenueCatId: userId,
      mixpanelId: normalizedMixpanelId,
    })
  } catch (error) {
    if (isRevenueCatMixpanelDistinctIdImmutableError(error)) {
      await markRevenueCatMixpanelDistinctIdSynced({
        revenueCatId: userId,
        mixpanelId: normalizedMixpanelId,
      })
      return
    }

    addBreadcrumb(
      'purchase',
      'revenuecat_mixpanel_id_sync_failed',
      { revenueCatId: userId },
      'warning',
    )
    console.warn('Failed to sync RevenueCat Mixpanel distinct ID', error)
  }
}
